import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore, useAppStore, useOnboardingStore } from '@/stores'
import { hasAllPermissions, hasAnyPermission } from "@/lib/filters.js";
import NProgress from 'nprogress'
import WrapperLayout from '@/layouts/WrapperLayout.vue'
import authRoutes from './auth.routes.js'
import DashboardLayout from "@/layouts/DashboardLayout.vue";
import WorkspaceRoutes from './workspace.routes.js'
import OnboardingRoutes from './onboarding.routes.js'
import QuizRoutes from './quiz.routes.js'
import DocumentSignRoutes from './document-sign.routes.js'
import AuditeeRoutes from './auditee.routes.js'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }

    // Restore browser back/forward position instantly.
    if (savedPosition) {
      return { ...savedPosition, behavior: 'auto' }
    }

    // Instant jump — smooth scroll on long onboarding forms felt like a hang.
    return { left: 0, top: 0, behavior: 'auto' }
  },
  routes: [
    {
      path: '/',
      component: WrapperLayout,
      children: [
        {
          path: '/',
          component: () => import('../views/handle-redirections.vue'),
          meta: { auth: true, allowAuthenticated: true }
        },
        OnboardingRoutes,
        {
          path: '/',
          component: DashboardLayout,
          children: [
            WorkspaceRoutes,
            AuditeeRoutes,
            {
              path: '/not-allowed',
              name: 'not-allowed',
              component: () => import('../views/no-permission.vue'),
              meta: { title: 'Permission Denied' },
            },
            {
              path: '/:pathMatch(.*)*',
              name: '404',
              component: () => import('../views/page-not-found.vue'),
              meta: { title: 'Page Not Found', auth: true, allowAuthenticated: true },
            },
          ]
        },
        QuizRoutes,
        DocumentSignRoutes,
        {
          path: '/page-not-found',
          name: 'page-not-found',
          meta: {
            title: 'Page Not Found'
          },
          component: () => import('../views/page-not-found.vue')
        },
      ]
    },
    ...authRoutes
  ]
})

router.beforeEach((to, from, next) => {
  document.title = to.meta.title || ''
  NProgress.start()

  const authStore = useAuthStore()

  // 0️⃣ After Dashboard / Invite exit — block browser Back into onboarding
  if (to.meta.onboardingFlow) {
    const onboardingStore = useOnboardingStore()
    if (onboardingStore.exited) {
      const workspaceId =
        onboardingStore.workspaceId ??
        authStore.user?.workspace_id ??
        authStore.defaultWorkspaceId
      return next(
        workspaceId
          ? { path: `/workspaces/${workspaceId}/dashboard`, replace: true }
          : { path: '/workspaces', replace: true },
      )
    }
  }

  // 1️⃣ Auth check — protect authenticated-only routes
  if (to.meta.auth && !authStore.isAuthenticated) {
    authStore.redirectAfterLogin = to.fullPath
    return next('/login')
  }

  // 2️⃣ Guest-only routes (login, signup, etc.) — bounce authenticated users
  // Redirect via `/` so handle-redirections can wait for profile and pick the
  // correct destination (admin dashboard, workspace CAC, invite/welcome, etc.)
  if (to.meta.guest && authStore.isAuthenticated) {
    return next('/')
  }

  // Public route
  if (!to.meta.auth) return next()

  // 3️⃣ Super admin shortcut
  if (authStore.isSuperAdmin) {
    return next()
  }

  // 4️⃣ Auth-only routes (dashboard, profile, etc.)
  if (to.meta.allowAuthenticated) {
    return next()
  }

  // 5️⃣ Permission checks
  const perms = to.meta.permissions

  if (perms) {
    if (perms.any && hasAnyPermission(perms.any)) {
      return next()
    }

    if (perms.all && hasAllPermissions(perms.all)) {
      return next()
    }

    return next('/not-allowed')
  }

  // 6️⃣ Authenticated but route is restricted and no rule matched
  return next('/not-allowed')
})

router.afterEach((to) => {
  NProgress.done()
  const hasWorkspaceDetails = to.matched.some(r => r.name === 'workspaces-details');
  const appStore = useAppStore()
  appStore.UpdateIsInsideWorkspace(hasWorkspaceDetails)
})

export default router
